import type { PackRecord, ProfileRecord } from '../types';

interface DownloadState {
  profiles: Record<string, number>;
  packs: Record<string, number>;
  revision: number;
}

const STORAGE_KEY = 'retro-profiles-downloads-v1';
const listeners = new Set<() => void>();
let memoryState: DownloadState = { profiles: {}, packs: {}, revision: 0 };
let storageUnavailable = false;
let hydrated = false;

function stateFromJson(value: string | null, fallbackRevision = 0): DownloadState {
  const parsed = value ? (JSON.parse(value) as Partial<DownloadState>) : {};
  return {
    profiles: parsed.profiles ?? {},
    packs: parsed.packs ?? {},
    revision: Number(parsed.revision) || fallbackRevision,
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    try {
      memoryState = stateFromJson(event.newValue, memoryState.revision + 1);
    } catch {
      memoryState = { profiles: {}, packs: {}, revision: memoryState.revision + 1 };
    }
    hydrated = true;
    listeners.forEach((listener) => listener());
  });
}

function readState(): DownloadState {
  if (hydrated || typeof window === 'undefined' || storageUnavailable) return memoryState;
  hydrated = true;
  try {
    memoryState = stateFromJson(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    storageUnavailable = true;
  }
  return memoryState;
}

function writeState(state: DownloadState): void {
  memoryState = state;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      storageUnavailable = true;
    }
  }
  listeners.forEach((listener) => listener());
}

export function downloadCountForProfile(profile: ProfileRecord): number {
  return profile.baseDownloads + (readState().profiles[profile.id] ?? 0);
}

export function downloadCountForPack(pack: PackRecord): number {
  return pack.baseDownloads + (readState().packs[pack.id] ?? 0);
}

export function incrementProfileDownload(profileId: string): void {
  const state = readState();
  writeState({
    ...state,
    profiles: { ...state.profiles, [profileId]: (state.profiles[profileId] ?? 0) + 1 },
    revision: state.revision + 1,
  });
}

export function incrementPackDownload(packId: string): void {
  const state = readState();
  writeState({
    ...state,
    packs: { ...state.packs, [packId]: (state.packs[packId] ?? 0) + 1 },
    revision: state.revision + 1,
  });
}

export function subscribeToDownloadCounts(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function downloadCountsSnapshot(): number {
  return readState().revision;
}

export function serverDownloadCountsSnapshot(): number {
  return 0;
}

export function formatDownloads(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}
